import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LEGAL, SUPPORT_EMAIL, legalIsDraft, TERMS_PATH, PRIVACY_PATH } from '../../lib/legal'
import memoarLogo from '../../assets/memoar-logo.png'

/**
 * The frame both legal documents share (SOL-197). Public, and rendered the same
 * signed in or out: Stripe's checkout links here, as does the landing.
 */
export function LegalPage({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  const draft = legalIsDraft()
  return (
    <div className="legal">
      <header className="legal__header">
        <Link to="/" className="legal__logo">
          <img src={memoarLogo} alt="" className="legal__logo-img" />
          <span className="legal__logo-text">memoar</span>
        </Link>
      </header>
      <main className="legal__main">
        {draft && (
          <p className="legal__draft" role="note">
            Draft under legal review. This document is not yet in force.
          </p>
        )}
        <h1 className="legal__title">{title}</h1>
        <p className="legal__meta">
          Effective <Fact value={LEGAL.effectiveDate} label="effective date" />
        </p>
        {children}
      </main>
      <LegalFooter />
    </div>
  )
}

/**
 * A fact from `LEGAL`. Missing, it renders as a visible placeholder rather than
 * as nothing: a sentence with a hole in it reads as complete.
 */
export function Fact({ value, label }: { value: string | null; label: string }): JSX.Element {
  if (value !== null) return <>{value}</>
  return <span className="legal__pending">[{label}: to be confirmed]</span>
}

/**
 * A business decision the draft cannot make, such as how long a Memoir is kept.
 * Rendered inline so the lawyer reads it in context. legal.test.tsx refuses a
 * document that is no longer a draft but still carries one of these.
 */
export function Pending({ children }: { children: ReactNode }): JSX.Element {
  return <span className="legal__pending">[To decide: {children}]</span>
}

/** The support address as a link, for help text outside the legal pages (SOL-200). */
export function SupportEmail(): JSX.Element {
  return <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
}

/** The contact address as a link, or its placeholder. */
export function ContactEmail(): JSX.Element {
  if (LEGAL.contactEmail === null) return <Fact value={null} label="contact email" />
  return <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>
}

/** Terms, Privacy and contact. Used on the signup screen, in the app, and here. */
export function LegalFooter({ className = '' }: { className?: string }): JSX.Element {
  return (
    <nav className={`legal-footer ${className}`.trim()} aria-label="Legal">
      <Link to={TERMS_PATH}>Terms of Service</Link>
      <Link to={PRIVACY_PATH}>Privacy Policy</Link>
      {LEGAL.contactEmail && <a href={`mailto:${LEGAL.contactEmail}`}>Contact</a>}
    </nav>
  )
}
